/**
 * "Ask which property is open" — a one-line channel from whatever learns that
 * the tab may be showing a property no longer open, to the shell that asks.
 *
 * The open property is server-side state every tab, window and device of a
 * user shares, and a page reads it once. The shell asks `/api/me` again
 * whenever the tab is looked at (`App.tsx`, `useOpenPropertyCheck`); what it
 * cannot see is a request the service refused for naming a property that is
 * no longer open (`ACTIVE_CONNECTION_CHANGED`, read in
 * `services/loadedConnection.ts`), or a page about to read the open property
 * beside what it loaded — the strategy editor opening. Those are said from
 * services and pages with no other reason to know the shell, so a module-level
 * channel is the smaller thing, as with `syncStatusEvents.ts`.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Ask the shell to find out which property is open now and compare it with the tab's. */
export function requestOpenPropertyCheck(): void {
    for (const listener of [...listeners]) listener();
}

/** Returns the unsubscribe function, for an effect's cleanup. */
export function onOpenPropertyCheckRequested(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
